import type { QueryKey } from '@tanstack/react-query'

import { entityKeys } from './entity-queries'

export type SettingTarget =
  | { kind: 'entity'; entityId: string; settingKey: string }
  | { kind: 'resource-default'; entityId: string; resourceType: string; settingKey: string }
  | {
      kind: 'resource'
      entityId: string
      resourceType: string
      resourceId: string
      settingKey: string
    }

export type SettingTargetProps =
  | { target: SettingTarget; entityId?: never; settingKey?: never }
  | { target?: never; entityId: string; settingKey: string }

export function settingTargetOf(props: SettingTargetProps): SettingTarget {
  return props.target === undefined
    ? { kind: 'entity', entityId: props.entityId, settingKey: props.settingKey }
    : props.target
}

export const resourceSettingTypeKey = (resourceType: string) =>
  ['resource-settings', resourceType] as const

export const resourceSettingKey = (resourceType: string, resourceId: string) =>
  [...resourceSettingTypeKey(resourceType), resourceId] as const

export const resourceSettingDefaultsKey = (entityId: string, resourceType: string) =>
  ['entities', 'resource-settings', entityId, resourceType] as const

export const settingKeys = {
  values: (target: SettingTarget): QueryKey => {
    switch (target.kind) {
      case 'entity':
        return entityKeys.settings(target.entityId)
      case 'resource-default':
        return resourceSettingDefaultsKey(target.entityId, target.resourceType)
      case 'resource':
        return resourceSettingKey(target.resourceType, target.resourceId)
    }
  },
  history: (target: SettingTarget, criteria: unknown = {}): QueryKey => {
    switch (target.kind) {
      case 'entity':
        return entityKeys.settingHistory(target.entityId, target.settingKey, criteria)
      case 'resource-default':
        return [
          ...entityKeys.settingHistoryByEntity(target.entityId),
          target.resourceType,
          target.settingKey,
        ]
      case 'resource':
        return [
          ...resourceSettingKey(target.resourceType, target.resourceId),
          'history',
          target.settingKey,
        ]
    }
  },
  historyRoot: (target: SettingTarget): QueryKey =>
    target.kind === 'resource'
      ? resourceSettingKey(target.resourceType, target.resourceId)
      : entityKeys.settingHistoryByEntity(target.entityId),
}
